//quick-add-bulk.js

if (!customElements.get('quick-add-bulk')) {
  customElements.define(
    'quick-add-bulk',
    class QuickAddBulk extends BulkAdd {
      constructor() {
        super();
        this.quantity = this.querySelector('quantity-input');
        this.abortController = null;
        this.optimisticSnapshot = null;

        const BULK_DEBOUNCE_TIMER = 120;
        const debouncedOnChange = debounce((event) => {
          const inputValue = parseInt(event.target.value);
          const variantId = event.target.dataset.index;
          
          // Apply optimistic update immediately
          this.applyOptimisticUpdate(variantId, inputValue);
          
          if (inputValue === 0) {
            this.startQueue(variantId, inputValue);
          } else {
            this.validateQuantity(event);
          }
        }, BULK_DEBOUNCE_TIMER);

        this.addEventListener('change', debouncedOnChange.bind(this));
        this.listenForActiveInput();
        this.listenForKeydown();
        this.lastActiveInputId = null;
        const pageParams = new URLSearchParams(window.location.search);
        window.pageNumber = decodeURIComponent(pageParams.get('page') || '');
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (
            event.source === 'quick-add' ||
            (event.cartData.items && !event.cartData.items.some((item) => item.id === parseInt(this.dataset.index))) ||
            (event.cartData.variant_id && !(event.cartData.variant_id === parseInt(this.dataset.index)))
          ) {
            return;
          }
          // If its another section that made the update
          this.onCartUpdate().then(() => {
            this.listenForActiveInput();
            this.listenForKeydown();
          });
        });
      }

      disconnectedCallback() {
        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      getInput() {
        return this.querySelector('quantity-input input');
      }

      selectProgressBar() {
        return this.querySelector('.progress-bar-container');
      }

      listenForActiveInput() {
        if (!this.classList.contains('hidden')) {
          this.getInput().addEventListener('focusin', (event) => event.target.select());
        }
        this.isEnterPressed = false;
      }

      listenForKeydown() {
        this.getInput().addEventListener('keydown', (event) => {
          if (event.key === 'Enter') {
            this.getInput().blur();
            this.isEnterPressed = true;
          }
        });
      }

      cleanErrorMessageOnType(event) {
        event.target.addEventListener(
          'keypress',
          () => {
            event.target.setCustomValidity('');
          },
          { once: true }
        );
      }

      onCartUpdate() {
        return new Promise((resolve, reject) => {
          const sectionIdSource = this.closest('.collection-quick-add-bulk') || document.getElementById('product-grid');
          const sectionId = sectionIdSource ? sectionIdSource.dataset.id : '';
          fetch(`${this.getSectionsUrl()}?section_id=${sectionId}`)
            .then((response) => response.text())
            .then((responseText) => {
              const html = new DOMParser().parseFromString(responseText, 'text/html');
              const sourceQty = html.querySelector(
                `#quick-add-bulk-${this.dataset.id}-${sectionId}`
              );
              if (sourceQty) {
                this.innerHTML = sourceQty.innerHTML;
              }
              resolve();
            })
            .catch((e) => {
              console.error(e);
              reject(e);
            });
        });
      }

      updateMultipleQty(items) {
        // Cancel any in-flight request
        if (this.abortController) {
          this.abortController.abort();
        }
        this.abortController = new AbortController();
        
        const ids = Object.keys(items);
        const snapshot = this.optimisticSnapshot;
        
        const body = JSON.stringify({
          updates: items,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: this.getSectionsUrl(),
        });

        const request = fetch(`${routes.cart_update_url}`, { 
          ...fetchConfig(), 
          ...{ body },
          signal: this.abortController.signal
        })
          .then((response) => {
            if (!response.ok) throw new Error('Network response was not ok');
            return response.text();
          })
          .then((state) => {
            const parsedState = JSON.parse(state);
            this.renderSections(parsedState, ids);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'quick-add', cartData: parsedState });
            // Clear optimistic snapshot on success
            this.optimisticSnapshot = null;
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              return;
            }
            // Rollback optimistic update on error
            if (snapshot) {
              this.rollbackOptimisticUpdate(snapshot.variantId, snapshot.previousQuantity);
            }
          })
          .finally(() => {
            this.selectProgressBar()?.classList.add('hidden');
            if (this.abortController && !this.abortController.signal.aborted) {
              this.abortController = null;
            }
          });

        return request;
      }

      applyOptimisticUpdate(variantId, quantity) {
        const input = this.getInput();
        if (!input) return;

        const previousQuantity = parseInt(input.dataset.cartQuantity || input.value || 0, 10) || 0;
        const sanitizedQuantity = Math.max(parseInt(quantity, 10) || 0, 0);

        if (sanitizedQuantity === previousQuantity) {
          return;
        }

        // Store snapshot for rollback
        this.optimisticSnapshot = { variantId, previousQuantity };

        // Update UI immediately
        input.dataset.cartQuantity = sanitizedQuantity;
        input.value = sanitizedQuantity;
      }

      rollbackOptimisticUpdate(variantId, previousQuantity) {
        const input = this.getInput();
        if (!input) return;
        
        input.dataset.cartQuantity = previousQuantity;
        input.value = previousQuantity;
        this.optimisticSnapshot = null;
      }

      getSectionsToRender() {
        const container = this.closest('.collection-quick-add-bulk') || document.getElementById('product-grid');
        const sectionId = container?.dataset?.id || '';
        const id = `quick-add-bulk-${this.dataset.id}-${sectionId}`;
        const selector = `#quick-add-bulk-${this.dataset.id}-${sectionId}`;
        const sections = [];
        if (sectionId) {
          sections.push({ id, section: sectionId, selector });
        }
        sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });
        sections.push({ id: 'CartDrawer', selector: '#CartDrawer', section: 'cart-drawer' });
        return sections;
      }

      renderSections(parsedState, ids) {
        const intersection = this.queue.filter((element) => ids.includes(element.id));
        if (intersection.length !== 0) return;
        this.getSectionsToRender().forEach((section) => {
          const sectionElement = document.getElementById(section.id);
          if (
            sectionElement &&
            sectionElement.parentElement &&
            sectionElement.parentElement.classList.contains('drawer')
          ) {
            parsedState.items.length > 0
              ? sectionElement.parentElement.classList.remove('is-empty')
              : sectionElement.parentElement.classList.add('is-empty');

            setTimeout(() => {
              document.querySelector('#CartDrawer-Overlay').addEventListener('click', this.cart.close.bind(this.cart));
            });
          }
          const elementToReplace =
            sectionElement && sectionElement.querySelector(section.selector)
              ? sectionElement.querySelector(section.selector)
              : sectionElement;
          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          }
        });

        if (this.isEnterPressed) {
          this.querySelector(`#Quantity-${this.lastActiveInputId}`).select();
        }

        this.listenForActiveInput();
        this.listenForKeydown();
      }
    }
  );
}


// clas
// class QuickAddBulk extends BulkHandler {
//   constructor() {
//     super();
//     this.init();
//   }

//   init() {
//     document.querySelectorAll('.quick-add-bulk-button').forEach(btn => {
//       btn.addEventListener('click', () => this.handleBulkAdd(btn));
//     });
//   }

//   handleBulkAdd(button) {
//     const items = this.collectItems(button);
//     this.updateMultipleQty(items).then(cartState => {
//       this.renderSections(cartState, this.getSectionsToRender());
//     });
//   }

//   collectItems(button) {
//     // custom logic for bulk add
//     const productId = button.dataset.productId;
//     const quantity = parseInt(button.dataset.quantity) || 1;
//     return { [productId]: quantity };
//   }
// }

// customElements.define('quick-add-bulk', QuickAddBulk);
